import React, { useMemo } from 'react';
import { Circle, Marker, Tooltip } from 'react-leaflet';
import useGeometryEditing from './hooks/useGeometryEditing';

const MIN_RADIUS = 5;

/**
 * Circle renderer with draggable resize handle. Keeps geometry in sync with legacy radiusMeters.
 */
export default function CircleShape({
  cp,
  setSelectedCheckpointId,
  handleIcon,
  metersPerDegreeLat,
  metersPerDegreeLng,
  updateCheckpoint,
  suppressFocusNext,
  editing = true,
}) {
  const { geometry } = useGeometryEditing({ cp, updateCheckpoint });
  const centerLat = Number(cp?.lat);
  const centerLng = Number(cp?.lng);
  const hasCenter = Number.isFinite(centerLat) && Number.isFinite(centerLng);
  const centerLatLng = useMemo(
    () => (hasCenter ? [centerLat, centerLng] : null),
    [centerLat, centerLng, hasCenter],
  );
  const kind = `${cp?.kind ?? cp?.type ?? ''}`.toLowerCase();
  const color = kind === 'origin' ? '#f5c400' : kind === 'destination' ? '#28a745' : '#2e7be7';
  const radius =
    Math.max(MIN_RADIUS, Math.round(geometry?.radiusMeters ?? cp?.radiusMeters ?? 75)) || 75;
  const bearing = Number.isFinite(
    geometry?.angleRad ?? geometry?.handleBearing ?? cp?.handleBearing,
  )
    ? (geometry?.angleRad ?? geometry?.handleBearing ?? cp?.handleBearing)
    : 0;
  const handleLatLng = useMemo(() => {
    if (!hasCenter) return null;
    const lngFactor = metersPerDegreeLng(centerLat || 0) || metersPerDegreeLat;
    const deltaLat = (radius * Math.cos(bearing)) / metersPerDegreeLat;
    const deltaLng = lngFactor ? (radius * Math.sin(bearing)) / lngFactor : 0;
    return [centerLat + deltaLat, centerLng + deltaLng];
  }, [bearing, centerLat, centerLng, hasCenter, metersPerDegreeLat, metersPerDegreeLng, radius]);

  if (!centerLatLng) return null;

  const applyRadius = (nextRadius, nextBearing) => {
    updateCheckpoint(cp.id, {
      radiusMeters: nextRadius,
      handleBearing: nextBearing,
      geometry: {
        ...geometry,
        type: geometry?.type || 'circle',
        radiusMeters: nextRadius,
        angleRad: nextBearing,
        handleBearing: nextBearing,
      },
    });
  };

  return (
    <>
      <Circle
        center={centerLatLng}
        radius={radius}
        pathOptions={{
          color,
          fillColor: color,
          fillOpacity: 0.2,
          weight: 1,
        }}
        eventHandlers={{
          click: () => setSelectedCheckpointId(cp.id),
        }}
      />
      {handleLatLng && editing && (
        <Marker
          position={handleLatLng}
          draggable={true}
          icon={handleIcon}
          eventHandlers={{
            drag: (e) => {
              suppressFocusNext.current = true;
              const newPos = e.target.getLatLng();
              const dLat = newPos.lat - centerLat;
              const dLng = newPos.lng - centerLng;
              const lngFactor = metersPerDegreeLng(centerLat || 0) || metersPerDegreeLat;
              const metersLat = dLat * metersPerDegreeLat;
              const metersLng = dLng * lngFactor;
              const meters = Math.sqrt(metersLat * metersLat + metersLng * metersLng);
              const bearingRad = Math.atan2(metersLng, metersLat);
              applyRadius(Math.max(MIN_RADIUS, Math.round(meters)), bearingRad);
            },
            click: () => {
              suppressFocusNext.current = true;
              setSelectedCheckpointId(cp.id);
            },
          }}
        >
          <Tooltip className="responder-tooltip" direction="top" offset={[0, -10]} opacity={0.8}>
            Drag to resize
          </Tooltip>
        </Marker>
      )}
    </>
  );
}
